import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { MouseEventHandler, ReactNode } from 'react';

import type { MetricValues, SecondaryDimensionValues } from '@revelio/core';
import { FormatType, getFormatter, TableChart } from '@revelio/replots';

import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { getColumnDimensionLabel } from '../../ChartConfig/utils/table-columns';
import { MultiMetricProvider } from '../../DataProvider/MultiMetricProvider';
import {
  DataProviderQueryStatus,
  type DataProviderResult,
} from '../../DataProvider/types';
import type { ResolvedData } from '../../DataResolver/resolve-items';
import type { TableColumnConfig } from '../../PageConfigProvider/types/item';
import { ChartFallback } from '../ChartFallback';
import { useChart } from '../ChartProvider';
import { ChartCard } from '../ChartRoot/Chart';
import { PrimaryEntitySwitcher } from '../chart-switcher/PrimaryEntitySwitcher';
import styles from './custom-table-chart.module.css';
import {
  type CustomTableRow,
  convertByMetricToTableRows,
} from './utils/convert-by-metric-to-table-rows';
import {
  convertTableDataToTableRows,
  getTableColumnRowKeys,
} from './utils/convert-table-data-to-table-rows';
import { deriveQueryFromColumns } from './utils/table-columns-to-query';

const columnRowKey = (
  column: TableColumnConfig,
  dimensionSource?: SecondaryDimensionValues
): string => {
  if (column.kind === 'variable') return column.metric;
  return column.dimension === dimensionSource
    ? 'dimension'
    : 'secondaryDimension';
};

const columnHeader = (column: TableColumnConfig): string => {
  if (column.label) return column.label;
  return column.kind === 'dimension'
    ? getColumnDimensionLabel(column.dimension)
    : column.metric;
};

/** Renders a variable cell with its configured number format (default: SI). */
const variableCell =
  (format: FormatType) =>
  (info: CellContext<CustomTableRow, unknown>): string => {
    const value = info.getValue();
    if (typeof value !== 'number') {
      return value === null || value === undefined ? '' : String(value);
    }
    return getFormatter(format)(value);
  };

const getDefaultFormat = (metric: MetricValues): FormatType => {
  switch (metric) {
    case 'headcount':
      return FormatType.INTEGER;
    case 'growth_rate':
    case 'hiring_rate':
    case 'attrition_rate':
      return FormatType.PERCENTAGE;
    case 'tenure':
      return FormatType.YEAR;
    case 'salary':
    case 'screener_salary':
      return FormatType.CURRENCY;
    default:
      return FormatType.DECIMAL;
  }
};

const buildColumnDefs = (
  columns: TableColumnConfig[],
  rowKeys: readonly string[]
): ColumnDef<CustomTableRow, unknown>[] =>
  columns.map((column, index) => {
    const def: ColumnDef<CustomTableRow, unknown> = {
      id: column.id,
      accessorKey: rowKeys[index],
      header: columnHeader(column),
    };
    if (column.kind === 'variable') {
      const format = column.format ?? getDefaultFormat(column.metric);
      def.cell = variableCell(format);
    }
    return def;
  });

export const CustomTableChart = ({
  primaryDimension,
  data,
  onClick,
}: {
  primaryDimension?: PrimaryFilterId;
  data?: ResolvedData;
  onClick?: MouseEventHandler<HTMLDivElement>;
}) => {
  const { item } = useChart();

  const columns = item?.plot?.tableColumns ?? [];
  const {
    dimensionSource,
    isDimensionSourcePrimary,
    secondaryDimension,
    metrics,
  } = deriveQueryFromColumns(columns, primaryDimension);

  const { primaryFilter: primaryFilterState } =
    usePrimaryFilterState(primaryDimension);

  const primaryEntityOptions = primaryFilterState?.primarySelections ?? [];

  const switcher =
    !isDimensionSourcePrimary &&
    primaryDimension &&
    primaryEntityOptions?.length > 1 ? (
      <PrimaryEntitySwitcher
        isSingleEntityChart
        primaryFilterId={primaryDimension}
      />
    ) : null;

  const renderTable = (
    columnDefs: ColumnDef<CustomTableRow, unknown>[],
    { data: rows, status, error }: DataProviderResult<CustomTableRow[]>
  ) => {
    if (status !== DataProviderQueryStatus.Success) {
      return (
        <ChartFallback
          status={status}
          errorMessage={error?.message}
          onClick={onClick}
        />
      );
    }

    if (!rows) {
      return (
        <ChartFallback
          status={DataProviderQueryStatus.Success}
          errorMessage="No data available"
          onClick={onClick}
        />
      );
    }

    return (
      <Table
        data={rows}
        columns={columnDefs}
        header={switcher}
        onClick={onClick}
      />
    );
  };

  if (data) {
    const { data: table, ...result } = data;
    return renderTable(
      buildColumnDefs(columns, getTableColumnRowKeys(columns, table)),
      { ...result, data: table && convertTableDataToTableRows(table) }
    );
  }

  return (
    <MultiMetricProvider
      primaryDimension={primaryDimension}
      dimensionSource={isDimensionSourcePrimary ? 'primary' : dimensionSource}
      metrics={metrics}
      secondaryDimension={secondaryDimension}
      chartType="table"
    >
      {({ data, ...result }) =>
        renderTable(
          buildColumnDefs(
            columns,
            columns.map((column) => columnRowKey(column, dimensionSource))
          ),
          {
            ...result,
            data: data && convertByMetricToTableRows(data.byMetric),
          }
        )
      }
    </MultiMetricProvider>
  );
};

const Table = ({
  data,
  columns,
  header,
  onClick,
}: {
  data: CustomTableRow[];
  columns?: ColumnDef<CustomTableRow, unknown>[];
  header?: ReactNode;
  onClick?: MouseEventHandler<HTMLDivElement>;
}) => {
  return (
    <ChartCard onClick={onClick} isRelative>
      <div className={styles.tableBody}>
        {header && (
          <div className={styles.headerBar}>
            <div className={styles.headerContent}>{header}</div>
          </div>
        )}
        <div
          className={styles.tableArea}
          data-has-header={header ? true : undefined}
        >
          <TableChart data={data} columns={columns} />
        </div>
      </div>
    </ChartCard>
  );
};
